import React, { useState, useEffect, useMemo } from "react";
import cx from "classnames";
import { useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Heading } from "@foris/avocado-suite";
import { useParams } from "react-router-dom";
import intervalsItemsQuery from "@services/collections/intervals/graphql/intervalsItems.query.graphql";
import Range from "@common/components/Range/Range";
import { objectEmpty } from "@utils/validations";
import { DataQuery, IdRangeInput } from "@models/ISchema";
import { IParams } from "@models/IParams";
import css from "./weeksrange.module.scss";

export interface IWeeksRangeValues {
  minWeek: number;
  maxWeek: number;
  weekRange?: Array<number>;
}

export interface IWeeksRangeReturn {
  disabled: boolean;
  initialExternalWeeks?: { min: number; max: number };
  onRendered: (isRender: boolean) => void;
  onCallback: (value: IWeeksRangeValues) => void;
}

const getIntervalIndex = (intervals: any[], intervalRange: IdRangeInput) => {
  const { start, end } = intervalRange;
  const startIndex = intervals.findIndex(interval => interval.id === start);
  const endIndex = intervals.findIndex(interval => interval.id === end);
  return { startIndex, endIndex };
};

const WeeksRange: React.FC<IWeeksRangeReturn> = props => {
  const { onCallback, disabled, onRendered, initialExternalWeeks } = props;
  // Hooks
  const client = useApolloClient();
  const { scenario }: IParams = useParams();
  const { t } = useTranslation();
  // States for handle the weeks range
  const [weeks, setWeeks] = useState({});
  const [initialWeeks, setInitialWeeks] = useState({});
  const [data, setData] = useState<DataQuery | null>(null);

  const variablesWeeks = {
    query: intervalsItemsQuery,
    variables: {
      scenarioId: scenario,
    },
  };

  /**
   * Get weeks interval (min-max)
   * query data graph
   */
  const getWeeksInterval = async () => {
    try {
      const { data } = await client.query(variablesWeeks);
      const dataWeeks: DataQuery = data.data;
      setData(dataWeeks);
      if (dataWeeks?.intervals?.items?.length > 1) {
        const weeks = { min: 1, max: dataWeeks?.intervals?.items?.length };

        const { startIndex, endIndex } = getIntervalIndex(dataWeeks?.intervals?.items, {
          start: initialExternalWeeks?.min?.toString(),
          end: initialExternalWeeks?.max?.toString(),
        });

        setWeeks(
          initialExternalWeeks
            ? {
                min: startIndex + 1,
                max: endIndex + 1,
              }
            : weeks,
        );
        setInitialWeeks(weeks);
      }
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Get weeks interval (min-max)
   * query data cache
   */
  const getCacheWeeksInterval = async () => {
    try {
      const { data } = await client.readQuery(variablesWeeks);
      const dataWeeks: DataQuery = data.data;
      if (dataWeeks?.intervals?.items?.length > 1) {
        const weeks = { min: 1, max: dataWeeks?.intervals?.items?.length };
        const { startIndex, endIndex } = getIntervalIndex(dataWeeks?.intervals?.items, {
          start: initialExternalWeeks?.min?.toString(),
          end: initialExternalWeeks?.max?.toString(),
        });

        setWeeks(
          initialExternalWeeks
            ? {
                min: startIndex + 1,
                max: endIndex + 1,
              }
            : weeks,
        );
        setInitialWeeks(weeks);
      }
    } catch (error) {
      getWeeksInterval();
    }
  };

  const onChangeRange = (value: any) => {
    const dataWeek = data && data?.intervals?.items.slice(value[0] - 1, value[1]);
    setWeeks({ min: value[0], max: value[1] });
    onCallback({
      minWeek: parseInt(data?.intervals?.items?.[value[0] - 1]?.id),
      maxWeek: parseInt(data?.intervals?.items?.[value[1] - 1]?.id),
      weekRange: dataWeek.map((week: any) => parseInt(week?.id)),
    });
  };

  useEffect(() => {
    if (objectEmpty(initialWeeks)) {
      getCacheWeeksInterval();
    }
  });

  if (props.disabled) {
    if (!objectEmpty(initialWeeks) && !objectEmpty(weeks)) {
      if (JSON.stringify(weeks) !== JSON.stringify(initialWeeks)) {
        onCallback({
          minWeek: null,
          maxWeek: null,
        });
        setWeeks(initialWeeks);
      }
    }
  }

  const shouldRender = useMemo(
    () => !objectEmpty(initialWeeks) && !objectEmpty(weeks),
    [initialWeeks, weeks],
  );

  useEffect(() => {
    onRendered?.(shouldRender);
  }, [shouldRender]);

  return (
    <>
      {!objectEmpty(initialWeeks) && !objectEmpty(weeks) && (
        <div className={cx(css.sharedWeeks, "container-row")}>
          <Heading className={css.sharedWeeks_title} type="h4">
            {t("advanced-search.classroom.forms.weeks.title")}
          </Heading>
          <Range
            className="col_md_12 col_7"
            disabled={disabled}
            step={1}
            min={initialWeeks["min"]}
            max={initialWeeks["max"]}
            textRange="S"
            onChange={value => onChangeRange(value)}
            valuesRange={[weeks["min"], weeks["max"]]}
          />
        </div>
      )}
    </>
  );
};

export default WeeksRange;
